import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import IconDownSide from '../svg/IconDownSide'

const LABEL_MAP = {
  data: 'Data',
  account: 'Account',
  portfolio: 'Portfolios',
  portfolios: 'Portfolios',
  'portfolio-group': 'Portfolio-Groups',
  'portfolio-groups': 'Portfolio-Groups',
  assets: 'Assets',
  asset: 'Asset',
  issuers: 'Issuers',
  issuer: 'Issuer',
  prices: 'Prices',
  byod: 'BYOD',
  profile: 'Profile',
  setting: 'Setting',
  dashboard: 'Dashboard',
  add: 'Add',
  update: 'Update',
  allocation: 'Allocation',
  'manage-allocation': 'Manage Allocation',
}

const formatLabel = (segment) => {
  if (!segment) return ''

  if (LABEL_MAP[segment]) return LABEL_MAP[segment]

  return segment
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join('-')
}

/** Top-level areas that already show a page title; single crumb is noise. */
const BREADCRUMB_HIDDEN_PREFIXES = [
  '/tolerance-manager',
  '/analysis',
]

const shouldHideBreadcrumbs = (pathname) => {
  const path = (pathname.split('?')[0] || '').replace(/\/$/, '') || '/'
  // Plain /allocation only — hide redundant single crumb; show /allocation/manage-allocation
  if (path === '/allocation') return true
  return BREADCRUMB_HIDDEN_PREFIXES.some(
    (base) => path === base || path.startsWith(`${base}/`)
  )
}

const Breadcrumbs = () => {
  const location = useLocation()

  if (shouldHideBreadcrumbs(location.pathname)) {
    return null
  }

  // W64: a record id is not a place. Detail routes carry one (/compliance/
  // mandates/:id, /compliance/rules/:id, /compliance/documents/:id), and it
  // rendered as a 24-character hex string in the trail — the page itself
  // names the record, so the id says nothing a reader can use.
  const isRecordId = (segment) => /^[0-9a-f]{24}$/i.test(segment)
  const pathnames = location.pathname.split('/').filter(Boolean).filter((segment) => !isRecordId(segment))

  const getCrumbConfig = (segments) => {
    const fullPath = `/${segments.join('/')}`

    // default: all non-clickable
    const crumbs = segments.map((segment, index) => ({
      key: `${segment}-${index}`,
      label: formatLabel(segment),
      clickable: false,
      to: null,
    }))

    // ---------- ALLOCATION / MANAGE-ALLOCATION ----------
    // /allocation/manage-allocation — first crumb links back to /allocation
    if (
      fullPath === '/allocation/manage-allocation' ||
      fullPath.startsWith('/allocation/manage-allocation/')
    ) {
      if (crumbs[0]) {
        crumbs[0] = {
          ...crumbs[0],
          label: 'Allocation',
          clickable: true,
          to: '/allocation',
        }
      }
      return crumbs
    }

    // ---------- PORTFOLIO (client area) ----------
    // /portfolio/:portfolioGroupSlug
    // /portfolio/:portfolioGroupSlug/:portfolioSlug
    if (fullPath.startsWith('/portfolio/')) {
      // First crumb ("Portfolios") should go to home "/"
      if (crumbs[0]) {
        crumbs[0] = {
          ...crumbs[0],
          clickable: true,
          to: '/',
        }
      }

      // Second crumb should go to "/portfolio/:portfolioGroupSlug"
      if (crumbs[1] && segments[1]) {
        crumbs[1] = {
          ...crumbs[1],
          clickable: true,
          to: `/portfolio/${segments[1]}`,
        }
      }

      return crumbs
    }

    // ---------- DATA / PORTFOLIOS ----------
    // /data/portfolios
    // /data/portfolios/add
    // /data/portfolios/update/:id
    if (
      fullPath === '/data/portfolios' ||
      fullPath.startsWith('/data/portfolios/add') ||
      fullPath.startsWith('/data/portfolios/update/')
    ) {
      if (crumbs[1]) {
        crumbs[1] = {
          ...crumbs[1],
          label: 'Portfolios',
          clickable: true,
          to: '/data/portfolios',
        }
      }
      return crumbs
    }

    // ---------- DATA / PORTFOLIO-GROUPS ----------
    // /data/portfolio-groups
    // /data/portfolio-groups/add
    // /data/portfolio-groups/update/:id
    if (
      fullPath === '/data/portfolio-groups' ||
      fullPath.startsWith('/data/portfolio-groups/add') ||
      fullPath.startsWith('/data/portfolio-groups/update/')
    ) {
      if (crumbs[1]) {
        crumbs[1] = {
          ...crumbs[1],
          label: 'Portfolio-Groups',
          clickable: true,
          to: '/data/portfolio-groups',
        }
      }
      return crumbs
    }

    // ---------- DATA / ASSETS DASHBOARD ----------
    // /data/assets/dashboard
    if (fullPath === '/data/assets/dashboard') {
      if (crumbs[1]) {
        crumbs[1] = {
          ...crumbs[1],
          label: 'Assets',
          clickable: true,
          to: '/data/assets/dashboard',
        }
      }
      return crumbs
    }

    // ---------- DATA / ASSET ADD / UPDATE ----------
    // /data/asset/add
    // /data/asset/update/:id
    if (
      fullPath.startsWith('/data/asset/add') ||
      fullPath.startsWith('/data/asset/update/')
    ) {
      if (crumbs[1]) {
        crumbs[1] = {
          ...crumbs[1],
          label: 'Asset',
          clickable: true,
          to: '/data/assets/dashboard',
        }
      }
      return crumbs
    }

    // ---------- DATA / ISSUERS DASHBOARD ----------
    // /data/issuers/dashboard
    if (fullPath === '/data/issuers/dashboard') {
      if (crumbs[1]) {
        crumbs[1] = {
          ...crumbs[1],
          label: 'Issuers',
          clickable: true,
          to: '/data/issuers/dashboard',
        }
      }
      return crumbs
    }

    // ---------- DATA / ISSUER ADD / UPDATE ----------
    // /data/issuer/add
    // /data/issuer/update/:id
    if (
      fullPath.startsWith('/data/issuer/add') ||
      fullPath.startsWith('/data/issuer/update/')
    ) {
      if (crumbs[1]) {
        crumbs[1] = {
          ...crumbs[1],
          label: 'Issuer',
          clickable: true,
          to: '/data/issuers/dashboard',
        }
      }
      return crumbs
    }

    // ---------- NON-CLICKABLE CASES ----------
    // /data/prices
    // /data/byod
    // /account/profile
    // /account/setting
    return crumbs
  }

  const crumbs = getCrumbConfig(pathnames)

  if (!crumbs.length) return null

  return (
    <div className="breadcrunbs flex items-center gap-1">
      {crumbs.map((crumb, index) => {
        const isLast = index === crumbs.length - 1

        return (
          <React.Fragment key={crumb.key}>
            {crumb.clickable && !isLast ? (
              <Link
                to={crumb.to}
                className="text-xs text-theme font-semibold capitalize"
              >
                {crumb.label}
              </Link>
            ) : (
              <span
                className={`text-xs capitalize ${
                  isLast
                    ? 'text-textV2 font-500'
                    : 'text-theme font-semibold'
                }`}
              >
                {crumb.label}
              </span>
            )}

            {!isLast && (
              <span>
                <IconDownSide className="text-textV2 w-4 h44 -rotate-90" />
              </span>
            )}
          </React.Fragment>
        )
      })}
    </div>
  )
}

export default Breadcrumbs